import { DomainPresence } from 'src/Common/hooks/useDomainPresence';

/**
 * The domains whose whole control surface is one pod to raise and lower, and
 * whose presence carries `serving`.
 *
 * NOT `OrchestratorDomain`: opener/unwinder publish `nodeServing` about their
 * `-node` pod and never `serving`, so pointing this at them would peg the status
 * to `unknown` forever regardless of pod state. Same reason
 * `ensureOrchestratorBooted` has its own `StartableDomain`.
 */
export type ControlPlaneDomain = 'matching' | 'opportunities';

/**
 * `up` — a replica is READY, so requests land.
 * `starting` — scaled but no ready replica yet. Requests still fail.
 * `draining` — scaled to 0 while a dying pod still reports a ready replica.
 * `down` — 0 replicas.
 * `notServing` — no ready replica, and the SCALE read is the half that is
 *   unknown. Requests fail either way, so this is down for gating purposes; it
 *   is a state of its own only so the chip does not claim a replica count it
 *   never read.
 * `unknown` — the backend did not report enough to say. Never guessed at.
 */
export type ControlPlaneStatus =
  | 'up'
  | 'starting'
  | 'draining'
  | 'down'
  | 'notServing'
  | 'unknown';

/**
 * ONE derivation of a control pod's state from `booted` + `serving`, shared by
 * the chip and the boot gate.
 *
 * They used to derive it separately and disagreed in the drain window: the chip
 * tested `serving === true` first and rendered a filled green "UP" for a pod k8s
 * was tearing down, while the gate — testing `booted` — put up the "está
 * apagado, booteálo" banner right below it.
 *
 * `booted === false` WINS. `booted` is `spec.replicas`, `serving` is
 * `status.readyReplicas`, and on a scale-down the former hits 0 the instant the
 * patch lands while the latter keeps counting the terminating pod for the whole
 * termination grace. `deriveOpenerPower` orders its `nodeReady`/`nodeServing`
 * pair the same way, for the same window.
 *
 * Every branch is `=== true` / `=== false`, never truthiness: both fields are
 * OMITTED when the backend could not read the Deployment, and an unknown must
 * never be answered as up or down.
 */
export const deriveControlPlaneStatus = (
  presence: Pick<DomainPresence, 'booted' | 'serving'>
): ControlPlaneStatus => {
  const { booted, serving } = presence;

  if (booted === false) return serving === true ? 'draining' : 'down';
  if (serving === true) return 'up';
  // A positive `serving === false` is not an unknown: readyReplicas really is 0,
  // so requests really do fail, and the gate must close even when the scale read
  // is the half that failed. Fail-open is for UNKNOWNS only.
  if (serving === false) return booted === true ? 'starting' : 'notServing';
  return 'unknown';
};

/**
 * Does this status mean requests against the pod are failing right now?
 *
 * Everything except `up` and `unknown`. `unknown` is the ONLY fail-open value —
 * the others all rest on a positive `false` from one field or the other, and in
 * each of them the Service has no ready endpoint: none yet (`starting`), none
 * left (`draining`), none at all (`down`), or none readable alongside an
 * unreadable scale (`notServing`).
 */
export const isControlPlaneDown = (status: ControlPlaneStatus): boolean =>
  status === 'down' ||
  status === 'starting' ||
  status === 'draining' ||
  status === 'notServing';
